import { useSendTransaction } from 'hooks/useTransaction'
import { useMemo } from 'react'
import { Trade } from 'state/routing/trades'
import { Address } from 'viem'

interface SwapCall {
  address: string
  calldata: string
  value: string
}

interface SwapCallEstimate {
  call: SwapCall
}

interface SuccessfulCall extends SwapCallEstimate {
  call: SwapCall
  gasEstimate: bigint
}

interface FailedCall extends SwapCallEstimate {
  call: SwapCall
  error: Error
}

// Returns a function that will execute a swap
export default function useSendSwapTransaction(
  account: string | null | undefined,
  chainId: number | undefined,
  publicClient: any | undefined,
  trade: Trade | undefined,
  swapCalls: SwapCall[]
): { callback: null | (() => Promise<{ hash: string }>) } {
  const { sendTransactionAsync } = useSendTransaction()
  return useMemo(() => {
    if (!trade || !publicClient || !account || !chainId) {
      return { callback: null }
    }

    return {
      callback: async function onSwap(): Promise<{ hash: string }> {
        // Estimate gas for all swap calls
        const estimatedCalls: SwapCallEstimate[] = await Promise.all(
          swapCalls.map(async (call) => {
            const { address, calldata, value } = call

            const isZeroValue = !value || value === '0' || value === '0x0' || value === '0x'

            try {
              const gasEstimate = await publicClient.estimateGas({
                account: account as Address,
                to: address as Address,
                data: calldata as Address,
                value: isZeroValue ? undefined : BigInt(value),
              })

              return {
                call,
                gasEstimate,
              }
            } catch (gasError: any) {
              console.debug('Gas estimate failed, trying eth_call to extract error', call)

              try {
                await publicClient.call({
                  account: account as Address,
                  to: address as Address,
                  data: calldata as Address,
                  value: isZeroValue ? undefined : BigInt(value),
                })

                console.debug('Unexpected successful call after failed estimate gas', call, gasError)
                const errorMessage = gasError.shortMessage || gasError.message || 'Gas estimation failed'
                return { call, error: new Error(errorMessage) }
              } catch (callError: any) {
                console.debug('Call threw error', call, callError)
                const errorMessage = callError.shortMessage || callError.message || 'Transaction simulation failed'
                return { call, error: new Error(errorMessage) }
              }
            }
          })
        )

        // Find the best call option
        let bestCallOption: SuccessfulCall | SwapCallEstimate | undefined = estimatedCalls.find(
          (el, ix, list): el is SuccessfulCall =>
            'gasEstimate' in el && (ix === list.length - 1 || 'gasEstimate' in list[ix + 1])
        )

        // Check if any calls errored
        if (!bestCallOption) {
          const errorCalls = estimatedCalls.filter((call): call is FailedCall => 'error' in call)
          if (errorCalls.length > 0) throw errorCalls[errorCalls.length - 1].error
          const firstNoErrorCall = estimatedCalls.find<SwapCallEstimate>(
            (call): call is SwapCallEstimate => !('error' in call)
          )
          if (!firstNoErrorCall) throw new Error('Unexpected error. Could not estimate gas for the swap.')
          bestCallOption = firstNoErrorCall
        }

        const {
          call: { address, calldata, value },
        } = bestCallOption

        const isZeroValue = !value || value === '0' || value === '0x0' || value === '0x'

        // Calculate gas limit with 20% margin
        const gasLimit = 'gasEstimate' in bestCallOption ? (bestCallOption.gasEstimate * 120n) / 100n : undefined

        try {
          const hash = await sendTransactionAsync({
            to: address as Address,
            data: calldata as Address,
            gas: gasLimit,
            value: isZeroValue ? undefined : BigInt(value),
            chainId,
          })

          return { hash }
        } catch (error: any) {
          console.log('Transaction error details:', {
            code: error.code,
            message: error.message,
            shortMessage: error.shortMessage,
            details: error.details,
          })

          // If user rejected the tx
          if (error?.code === 4001 || error?.name === 'UserRejectedRequestError') {
            throw new Error(error.shortMessage || error.message || 'User rejected transaction')
          } else {
            const actualError = error.shortMessage || error.message || 'Transaction failed'
            throw new Error(`Swap failed: ${actualError}`)
          }
        }
      },
    }
  }, [account, chainId, publicClient, swapCalls, trade, sendTransactionAsync])
}
